import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import type { StorefrontProduct, StorefrontStore } from "@/types/storefront";
import { useStorefrontNearbyStores, useStorefrontProductSearch } from "@/hooks/queries";

interface Location {
  label: string;
  area: string;
}

export interface GeoCoords {
  latitude: number;
  longitude: number;
}

export const PATNA_COORDS: GeoCoords = { latitude: 25.5941, longitude: 85.1376 };

const DEFAULT_LOCATION: Location = { label: "Detecting...", area: "Detecting" };
const FALLBACK_LOCATION: Location = { label: "Patna, Bihar", area: "Patna" };

interface LocationContextType {
  selectedLocation: Location;
  coords: GeoCoords;
  setSelectedLocation: (loc: Location) => void;
  setManualLocation: (label: string, area: string, latitude: number, longitude: number) => void;
  isDetecting: boolean;
  isLoadingStores: boolean;
  isLoadingProducts: boolean;
  nearbyStores: StorefrontStore[];
  nearbyProducts: StorefrontProduct[];
  getStoreProducts: (storeId: string) => StorefrontProduct[];
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

async function reverseGeocode(lat: number, lon: number): Promise<Location> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`,
      { headers: { "Accept-Language": "en" } },
    );
    if (!res.ok) throw new Error("Geocode failed");
    const data = await res.json();
    const addr = data.address || {};
    const area =
      addr.suburb ||
      addr.neighbourhood ||
      addr.city_district ||
      addr.town ||
      addr.city ||
      addr.county ||
      "Unknown";
    const city = addr.city || addr.town || addr.state_district || addr.state || "";
    const label = city && area !== city ? `${area}, ${city}` : area;
    return { label, area };
  } catch {
    return FALLBACK_LOCATION;
  }
}

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState<Location>(DEFAULT_LOCATION);
  const [coords, setCoords] = useState<GeoCoords>(PATNA_COORDS);
  const [isDetecting, setIsDetecting] = useState(true);

  const setManualLocation = useCallback((label: string, area: string, latitude: number, longitude: number) => {
    setSelectedLocation({ label, area });
    setCoords({ latitude, longitude });
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setSelectedLocation(FALLBACK_LOCATION);
      setCoords(PATNA_COORDS);
      setIsDetecting(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoords({ latitude, longitude });
        const loc = await reverseGeocode(latitude, longitude);
        setSelectedLocation(loc);
        setIsDetecting(false);
      },
      () => {
        setSelectedLocation(FALLBACK_LOCATION);
        setCoords(PATNA_COORDS);
        setIsDetecting(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  const nearbyRequest = useMemo(
    () => ({
      latitude: coords.latitude,
      longitude: coords.longitude,
      radiusKm: 15,
      page: 1,
      pageSize: 50,
    }),
    [coords],
  );

  const { data: storesData, isLoading: isLoadingStores } = useStorefrontNearbyStores(
    isDetecting ? null : nearbyRequest,
  );

  const { data: productsData, isLoading: isLoadingProducts } = useStorefrontProductSearch(
    { inStock: true, pageSize: 100, page: 1 },
    !isDetecting,
  );

  const nearbyStores = useMemo(() => {
    const items = storesData?.items ?? [];
    return [...items].sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
  }, [storesData]);

  const openStoreIds = useMemo(
    () => new Set(nearbyStores.filter((s) => s.isOpen).map((s) => s.id)),
    [nearbyStores],
  );

  const allProducts = productsData?.items ?? [];

  const nearbyProducts = useMemo(() => {
    if (openStoreIds.size === 0) return allProducts;
    return allProducts.filter((p) => openStoreIds.has(p.storeId));
  }, [allProducts, openStoreIds]);

  const getStoreProducts = useCallback(
    (storeId: string) => allProducts.filter((p) => p.storeId === storeId),
    [allProducts],
  );

  return (
    <LocationContext.Provider
      value={{
        selectedLocation,
        coords,
        setSelectedLocation,
        setManualLocation,
        isDetecting,
        isLoadingStores,
        isLoadingProducts,
        nearbyStores,
        nearbyProducts,
        getStoreProducts,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error("useLocation must be used within LocationProvider");
  return context;
};
